import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  labelClassName?: string;
  /**
   * Keeps the label for screen readers but takes it off the screen, for a field
   * whose heading already names it. Never omit `label` instead — a textarea
   * with no label at all is unusable without sight. Matches `DatePicker`'s prop
   * of the same name.
   */
  hideLabel?: boolean;
  error?: string;
  hint?: string;
  required?: boolean;
}

/** Multi-line input, styled to match `Input`. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, labelClassName, hideLabel = false, error, hint, className, id, rows = 3, required = false, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const errorId = `${textareaId}-error`;
  const hintId = `${textareaId}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={textareaId} className={cn('lbl', labelClassName, hideLabel && 'sr-only')}>
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={cn(
          'w-full resize-y rounded-field border bg-surface px-3 py-2.5 text-base text-ink',
          'placeholder:text-ink-3 transition-colors duration-150',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25',
          'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3',
          error ? 'border-crit focus:border-crit focus:ring-crit/25' : 'border-line-strong',
          className,
        )}
        {...props}
      />

      <FieldNote error={error} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
});
